import { Formik } from 'formik';
import { Form } from 'formik';
import keyBy from 'lodash/keyBy.js';
import noop from 'lodash/noop.js';
import { useState } from 'react';
import { useMemo } from 'react';
import React from 'react';
import { PurchasesResponse } from '../../api/fetchPurchases.js';
import { FormPagination } from '../../base-components/FormPagination.js';
import useModalState from '../_hooks/useModalState.js';
import { FormQueryParams } from './components/FormQueryParams.js';
import { InstantSubmit } from './components/InstantSubmit.js';
import { PageHeader } from './components/PageHeader.js';
import { Customer } from './Customer/Customer.js';
import { Filter } from './Filter/index.js';
import { useOnChange } from './hooks/useOnChange.js';
import { useInitialState } from './hooks/useQueryParams.js';
import { useScrollToAnchor } from './hooks/useScrollToAnchor.js';
import { List } from './List/index.js';
import { PlannedNav } from './PlannedPurchases/PlannedNav.js';
import { PurchasesState } from './Purchases.type.js';
import styles from './PurchasesContainer.module.scss';
import { Stats } from './Stats/index.js';
import { SortByOption } from './types.js';
import { useRouter } from 'next/router.js';
import { metaTags } from '../_dummyData/index.js';

const sortByOptions: SortByOption[] = [
  {
    label: 'дате публикации извещения',
    value: 'publish_date',
  },
  {
    label: 'дате окончания приёма заявок',
    value: 'phase_receive_finish_ts',
  },
  {
    label: 'минимальной цене лота',
    value: 'min_lot_start_price',
  },
  {
    label: 'максимальной цене лота',
    value: 'max_lot_start_price',
  },
];

export interface PurchasesProps extends PurchasesResponse {}

export const PurchasesContainer: React.FC<PurchasesProps> = ({
  announces,
  orgSection,
  reference,
  total,
  matches,
  selectableOptions,
}) => {
  const router = useRouter();
  const { initialState, emptyFilterState } = useInitialState();
  const [state, setState] = useState<PurchasesState>(initialState);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isRegionsModalVisible, showRegionsModal, hideRegionsModal] =
    useModalState();
  const showOrgSection = Boolean(orgSection?.orgPublicProps);
  const showPlanned = useMemo(
    () => Boolean(state.sectionOrg),
    [state.sectionOrg],
  );

  /*
   * При пагинации выполняем прокрутку к началу страницы, после блока фильтров
   */
  const [scrollAnchor, scrollToAnchor] = useScrollToAnchor();
  useOnChange(state.page, scrollToAnchor);

  const regionsByCode = useMemo(
    () => keyBy(reference.regions, 'regionCode'),
    [reference.regions],
  );

  const heading = useMemo(() => {
    if (orgSection?.orgTitle) {
      return `Тендеры ${orgSection?.orgTitle}`;
    }

    if (typeof router.query.section === 'string') {
      return metaTags[router.query.section].headings;
    }

    return 'Закупки';
  }, [orgSection?.orgTitle, router.query.section]);

  return (
    <div className={styles.container}>
      <PageHeader>{heading}</PageHeader>

      <Formik initialValues={state} onSubmit={noop}>
        <Form noValidate>
          {showOrgSection && <Customer orgSection={orgSection} />}
          {showPlanned && <PlannedNav orgTitle={orgSection?.orgTitle} />}
          <Filter
            reference={reference}
            selectableOptions={selectableOptions}
            matches={matches}
            emptyFilterState={emptyFilterState}
            isLoading={isLoading}
            isRegionsModalVisible={isRegionsModalVisible}
            showRegionsModal={showRegionsModal}
            sortByOptions={sortByOptions}
            total={total}
            hideRegionsModal={hideRegionsModal}
          />

          {scrollAnchor}

          <List announces={announces} regionsByCode={regionsByCode} />

          <div className={styles.pagination}>
            <FormPagination name="page" total={total} limit={10} />
          </div>

          <FormQueryParams
            state={state}
            emptyFilterState={emptyFilterState}
            setLoading={setLoading}
          />

          <InstantSubmit onChange={setState} />
        </Form>
      </Formik>
    </div>
  );
};
